import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Brain,
  Bell,
  Share2,
  ArrowRight,
  Coffee,
  Plane,
  Mic,
  Network,
} from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroAsset from "@/assets/solutions/founders/hero.webp.asset.json";
import memoryAsset from "@/assets/solutions/founders/memory.webp.asset.json";
import nudgeAsset from "@/assets/solutions/founders/nudge.webp.asset.json";
import profileAsset from "@/assets/solutions/founders/profile.webp.asset.json";

const hero = heroAsset.url;
const memory = memoryAsset.url;
const nudge = nudgeAsset.url;
const profile = profileAsset.url;

const heroAlt =
  "Founder's AI-powered contact profile on iPhone showing notes, tags, meeting history and follow-up tasks for a key investor";
const memoryAlt =
  "Banana calendar and pre-meeting briefs dashboard surfacing where you met each contact, past notes and AI-generated talking points";
const nudgeAlt =
  "Founder task management on iPhone with follow-up reminders, overdue alerts and Task Complete automation triggering personalized emails";
const profileAlt =
  "One shareable Banana profile link on iPhone with QR code, Apple and Google Wallet passes, and one-tap share to WhatsApp, LinkedIn and email";

export const Route = createFileRoute("/solutions/founders")({
  head: () => ({
    meta: [
      { title: "The founder's contact OS — banana.cards" },
      {
        name: "description",
        content:
          "Investors, partners, customers, hires — Banana remembers every conversation and nudges you the day they matter again.",
      },
      { property: "og:title", content: "The founder's contact OS — banana.cards" },
      {
        property: "og:description",
        content:
          "Investors, partners, customers, hires — Banana never lets you drop a relationship that matters.",
      },
      { property: "og:image", content: hero },
      { property: "og:url", content: "https://banana.cards/solutions/founders" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/solutions/founders" }],
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="For Founders"
      title="Every conversation"
      italic="matters."
      description="Your network is your moat. Banana is the second brain that remembers every coffee, intro and DM — and tells you when it's time to follow up."
      heroImage={hero}
      heroImageAlt={heroAlt}
      metrics={[
        { v: "3,200+", l: "Avg. contacts under management" },
        { v: "92%", l: "Of intros responded to within 48h" },
        { v: "1", l: "Profile across every channel" },
      ]}
      bullets={[
        {
          icon: Brain,
          title: "AI memory for every person",
          desc: "Notes, voice memos, LinkedIn updates, emails and meetings — auto-summarized into a single living profile per contact.",
        },
        {
          icon: Bell,
          title: "Nudges before it's awkward",
          desc: "Banana watches for funding rounds, job changes and silence streaks — and reminds you to reach out at the right moment.",
        },
        {
          icon: Share2,
          title: "One profile, every channel",
          desc: "NFC card, QR, Apple Wallet, email signature, LinkedIn — update once, sync everywhere. Always your current pitch.",
        },
      ]}
      deepDives={[
        {
          eyebrow: "Remember",
          title: "Never start a conversation with",
          italic: "\"remind me where we met?\"",
          description:
            "Every meeting transcribed, every voice memo summarized, every email surfaced — collapsed into a one-paragraph brief on each contact you're about to talk to.",
          points: [
            "AI notetaker for Zoom, Meet & Teams",
            "Voice memos → searchable notes",
            "Email & calendar history per contact",
            "Pre-meeting briefs in your inbox 15 min before",
          ],
          image: memory,
          imageAlt: memoryAlt,
        },
        {
          eyebrow: "Nudge",
          title: "Stay top of mind,",
          italic: "without being annoying.",
          description:
            "Set a relationship cadence per contact — weekly for co-founders, quarterly for angels, yearly for old colleagues — and Banana will surface them when it's time, with a draft message ready to go.",
          points: [
            "Per-contact follow-up cadences",
            "Job-change & funding-round alerts",
            "Birthday & anniversary reminders",
            "AI-drafted check-ins in your voice",
          ],
          image: nudge,
          imageAlt: nudgeAlt,
          reverse: true,
        },
        {
          eyebrow: "Share",
          title: "One link is all you need",
          italic: "to introduce yourself.",
          description:
            "Your Banana profile is your pitch, your calendar, your portfolio and your contact card — wrapped in a single tap or QR. Add it to your email signature once and you'll never re-paste a Calendly link again.",
          points: [
            "NFC cards in metal, wood, gold & PVC",
            "Apple & Google Wallet passes",
            "Email signature builder",
            "Branded link with your own domain",
          ],
          image: profile,
          imageAlt: profileAlt,
        },
      ]}
      howItWorks={[
        { title: "Import your network", desc: "Connect Gmail, LinkedIn CSV, iCloud and Calendly — Banana de-dupes and enriches in the background." },
        { title: "Pick your cadences", desc: "Tag investors, customers, friends. Set how often you want to stay in touch with each tag." },
        { title: "Let AI listen", desc: "Add the notetaker to your calls. Forward voice memos. Banana builds the memory for you." },
        { title: "Follow up on time", desc: "Open the daily \"who to reach out to\" digest and send the drafted message in two taps." },
      ]}
      capabilities={[
        "AI meeting notetaker (50+ languages)",
        "Voice memo → CRM in one step",
        "Investor & cap-table tagging",
        "LinkedIn & email enrichment",
        "Apple & Google Wallet passes",
        "Email signature builder",
        "Calendar booking links",
        "End-to-end encryption on notes",
      ]}
      faq={[
        {
          q: "Is this just a fancy address book?",
          a: "No — Banana is an active relationship manager. Address books store data; Banana watches for signals (new role, fresh funding, 90 days of silence) and tells you the moment a contact is worth a touchpoint, with a draft message ready in your tone.",
        },
        {
          q: "How does the AI memory stay accurate?",
          a: "Every meeting you join with the Banana notetaker, every voice memo you forward, and every email thread you tag gets summarized into the contact's profile. Old notes never get overwritten — they're stacked chronologically, so you can scroll the entire history.",
        },
        {
          q: "Can I keep some contacts private from my team?",
          a: "Yes. Founders running a company on Banana can mark contacts as personal — they stay out of the team CRM, off shared pipelines, and only visible to you. Investor lists are typically kept here.",
        },
        {
          q: "What happens to my data if I leave?",
          a: "You own it. Export every contact, note and recording as JSON or CSV at any time. Notes are end-to-end encrypted, and we never train AI models on your data.",
        },
        {
          q: "Do you have an iOS / Android app?",
          a: "Yes — and they're where most founders live. Native apps for iPhone and Android cover tap-to-share, voice notes, push reminders, and the daily \"who to reach out to\" digest.",
        },
      ]}
      outro={{
        title: "Your network is your",
        italic: "unfair advantage.",
        description: "Stop letting it decay in three CRMs, a Notes app and 14,000 LinkedIn connections. Bring it home.",
      }}
      afterFaq={<FounderUseCases />}
    />
  );
}

function FounderUseCases() {
  const items = [
    { i: Coffee, t: "Fundraising", d: "Track every investor convo, mark warm vs cold, get nudged the week before your next round.", to: "/platform/pipelines" },
    { i: Plane, t: "Conferences & travel", d: "Tap cards on the show floor, sync to CRM before you board, follow up before jetlag hits.", to: "/platform/events" },
    { i: Mic, t: "Podcast & press", d: "Save every host, journalist & producer in one tagged list. Re-pitch when there's news.", to: "/platform/crm" },
    { i: Network, t: "Hiring & advisors", d: "Keep a private bench of dream-team hires. Nudge yearly until the timing is right.", to: "/platform/contacts" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Founder use cases</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Every hat you wear, <em className="italic">covered.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Explore <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
